import { useRouter } from 'next/router'
import { useState, useEffect, FC } from 'react'

import { BlockContent } from '~/components/BlockContent'
import { useSetBootcampDataMutation } from '~/bootcamp/apis/bootcamp.api'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import MultipleChoice from '~/bootcamp/Activities/SanityActivities/MultipleChoice/MultipleChoice'
import FillInTheBlank from '~/bootcamp/Activities/SanityActivities/FillInTheBlank/FillInTheBlank'
import NumberLine from '~/bootcamp/Activities/SanityActivities/NumberLine/NumberLine'
import { ActivityQuestionType } from '~/types/bootcamp'
import { BootcampPageUrlParamsType } from '~/bootcamp/types/bootcamp.types'
import Activities from '~/bootcamp/Activities/Activities'
import bootcampAnalytics from '~/analytics/bootcamp/bootcampAnalytics'
import { BCAEvent } from '~/analytics/bootcamp/bootcampEvents.types'
import { selectBossHealth, selectMathEnergyEarned, setBossCurrentBehavior } from '~/bootcamp/slices/bootcamp-quiz.slice'
import styles from '~/shared/styles/SanityBlock.module.css'
import { BossBehaviors } from '~/bootcamp/Activities/types/activities.types'
import { useBootcampActivityLogging } from '~/bootcamp/hooks/useBootcampActivityLogging'
import { useSetBossProgressMutation } from '~/bootcamp/apis/set-data.api'
import ROUTES from '~/shared/constants/routes.const'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import { Labels } from '~/analytics/types/analytics.type'
import { selectVccSubBadgeImage } from '~/bootcamp/slices/vcc-data.slice'
import { useSetBadgeNotificationMutation } from '~/redux/apis/notification-api'
import CircleCombinations from '~/bootcamp/Activities/SanityActivities/CircleCombinations/CircleCombinations'
import Categorization from '~/bootcamp/Activities/SanityActivities/Categorization/Categorization'
import OrderElements from '~/bootcamp/Activities/SanityActivities/OrderElements/OrderElements'
import MatchingPairs from '~/bootcamp/Activities/SanityActivities/MatchingPairs/MatchingPairs'
import MeasureTheItem from '~/bootcamp/Activities/SanityActivities/MeasureTheItem/MeasureTheItem'

import QuizSubmitButton from './QuizSubmitButton'
import { QuizQuestionsWrapperProps } from './QuizProps.models'
import { Activity, QuizState } from './QuizData.models'

const RETRY_IN_PLACE_TYPES = [ActivityQuestionType.orderElements, ActivityQuestionType.matchingPairs]

const QuizQuestionsWrapper: FC<QuizQuestionsWrapperProps> = ({
  onQuit,
  onQuizStateUpdate = () => {},
  quizInitialState,
  onFail,
}): React.ReactElement => {
  const { push, asPath } = useRouter()
  const authenticated = useAppSelector(isAuthenticated)
  const [questionIdx, setQuestionIdx] = useState<number>(quizInitialState.completedQuestions)
  const [currentQuestion, setCurrentQuestion] = useState<Activity>(quizInitialState.questions[questionIdx].activity)
  const [quizState, setQuizState] = useState<QuizState | null>(null)
  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.XL })
  const bossHealth = useAppSelector(selectBossHealth)
  const { query } = useRouter()
  const { action, key: bootcampKey, level, moduleKey, subtopic } = query as unknown as BootcampPageUrlParamsType
  const [setBootcampData] = useSetBootcampDataMutation()
  const dispatch = useAppDispatch()
  const mathEnergy = useAppSelector(selectMathEnergyEarned)
  const [setBadgeNotification] = useSetBadgeNotificationMutation()
  const subBadge = useAppSelector(selectVccSubBadgeImage)
  const [setBossProgress] = useSetBossProgressMutation()

  useEffect(() => {
    setQuizState(quizInitialState)
  }, [quizInitialState])

  useEffect(() => {
    if (quizState && quizState.wrongAnswer > quizState.maxWrongAnswer && !!onFail) {
      // Interact with Bootcamp Boss for wrong answer
      dispatch(setBossCurrentBehavior(BossBehaviors.DEFEAT))
      onFail()
    } else if (quizState && quizState.completedQuestions === quizState.totalQuestions) {
      // Interact with Bootcamp Boss for victory
      dispatch(setBossCurrentBehavior(BossBehaviors.VICTORY))

      setBootcampData({
        bootcampId: bootcampKey,
        moduleId: moduleKey,
        levelId: level,
        levelPage: action,
        gymType: subtopic,
        awardMathEnergyOrbs: mathEnergy,
      })

      bootcampAnalytics.log(BCAEvent.WIN_BOSS_BATTLE, {
        category: subtopic,
        subject: bootcampKey,
        bootcamp: moduleKey,
        level,
        bootcamp_page_type: action,
      })

      setBossProgress({ category: subtopic, subject: bootcampKey, bootcampId: moduleKey })
      setBadgeNotification({
        subject: bootcampKey,
        category: subtopic,
        imagePath: subBadge?.srcKey || '',
      })
      onQuit()
    }
  }, [
    quizState,
    mathEnergy,
    onFail,
    onQuit,
    dispatch,
    bootcampKey,
    moduleKey,
    level,
    action,
    subtopic,
    setBootcampData,
    setBossProgress,
    setBadgeNotification,
    subBadge,
  ])

  const showNextQuestion = (): void => {
    if (questionIdx + 1 >= quizInitialState.totalQuestions) {
      return
    }
    const nextIndex = (questionIdx + 1) % quizInitialState.totalQuestions
    setQuestionIdx(nextIndex)
    setCurrentQuestion(quizInitialState.questions[nextIndex].activity)
  }

  const updateQuizState = (currentQuestionIsCorrect?: boolean, completedQuestions?: number): void => {
    if (!quizState) return
    const newQuizState = { ...quizState }
    newQuizState.wrongAnswer = currentQuestionIsCorrect ? newQuizState.wrongAnswer : newQuizState.wrongAnswer + 1
    newQuizState.questions[questionIdx].pass = currentQuestionIsCorrect ?? null
    newQuizState.completedQuestions = completedQuestions ?? 0
    newQuizState.bossHealth = bossHealth

    setQuizState({ ...newQuizState })
    onQuizStateUpdate({ ...newQuizState })
  }

  const activityLogger = useBootcampActivityLogging({
    questionType: currentQuestion.questionType,
    subtopic,
    bootcampKey,
    moduleKey,
    level,
    activityType: currentQuestion.type,
    activityName: currentQuestion.name,
  })

  const handleIsCorrectAnswer = (correct: boolean, questionId?: string): void => {
    if (!authenticated) {
      push({ pathname: ROUTES.LOGIN, query: { from: asPath } })
      return
    }

    if (correct === null || correct === undefined) return
    // These two are fixed in place, so a wrong attempt must not skip the question or count toward defeat.
    if (!correct && RETRY_IN_PLACE_TYPES.includes(currentQuestion.questionType)) return

    activityLogger(correct, questionId)
    bootcampAnalytics.log(BCAEvent.BOSS_FIGHT_CHECK_ANSWER, {
      status: correct ? Labels.CORRECT : Labels.INCORRECT,
      category: subtopic,
      subject: bootcampKey,
      bootcamp: moduleKey,
      level,
      bootcamp_page_type: action,
      activity_name: currentQuestion.name,
    })
    const nextQuestionIdx = questionIdx + 1
    // Interact with Bootcamp Boss for right answer
    if (quizState && nextQuestionIdx !== quizState.totalQuestions) {
      if (correct) dispatch(setBossCurrentBehavior(BossBehaviors.RIGHT))
      else if (quizState && quizState.wrongAnswer < quizState.maxWrongAnswer) {
        dispatch(setBossCurrentBehavior(BossBehaviors.WRONG))
      }
      showNextQuestion()
    }
    updateQuizState(correct, nextQuestionIdx)
  }

  function renderActivityByQuestionType(): React.ReactElement {
    if (currentQuestion.questionType === ActivityQuestionType.interactive && currentQuestion.name) {
      return (
        <Activities
          actionButtonsFC={QuizSubmitButton}
          activityName={currentQuestion.name}
          activityType={currentQuestion.type}
          answerState={null}
          autofocus={isDesktop}
          correctConsecutiveAnswersCount={1}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.multipleChoiceQuestion &&
      currentQuestion.multipleChoiceActivity &&
      currentQuestion.multipleChoiceActivity.length > 0
    ) {
      return (
        <MultipleChoice
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.multipleChoiceActivity}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.fillInTheBlanks &&
      currentQuestion.fillInTheBlankActivity &&
      currentQuestion.fillInTheBlankActivity.length > 0
    ) {
      return (
        <FillInTheBlank
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          keyboardLayoutId={currentQuestion.keyboardLayout}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.fillInTheBlankActivity}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.circleCombinations &&
      currentQuestion.circleCombinationsActivity &&
      currentQuestion.circleCombinationsActivity.length > 0
    ) {
      return (
        <CircleCombinations
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.circleCombinationsActivity}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.categorization &&
      currentQuestion.categorizationActivity &&
      currentQuestion.categorizationActivity.length > 0
    ) {
      return (
        <Categorization
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.categorizationActivity}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.orderElements &&
      currentQuestion.orderElementsActivity &&
      currentQuestion.orderElementsActivity.length > 0
    ) {
      return (
        <OrderElements
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.orderElementsActivity}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.matchingPairs &&
      currentQuestion.matchingPairsActivity &&
      currentQuestion.matchingPairsActivity.length > 0
    ) {
      return (
        <MatchingPairs
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.matchingPairsActivity}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.numberLine &&
      currentQuestion.numberLineActivity &&
      currentQuestion.numberLineActivity.length > 0
    ) {
      return (
        <NumberLine
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.numberLineActivity}
        />
      )
    } else if (
      currentQuestion.questionType === ActivityQuestionType.measureTheItem &&
      currentQuestion.measureTheItemActivity &&
      currentQuestion.measureTheItemActivity.length > 0
    ) {
      return (
        <MeasureTheItem
          actionButtonsFC={QuizSubmitButton}
          activityType={currentQuestion.type}
          answerState={null}
          difficultyLevel={currentQuestion.difficultyLevel}
          levelId={`${currentQuestion.level}`}
          onAnswerStateChange={handleIsCorrectAnswer}
          onNextQuestionClick={() => {}}
          questions={currentQuestion.measureTheItemActivity}
        />
      )
    } else return <></>
  }

  if (!currentQuestion) return <></>

  return (
    <>
      {currentQuestion?.description && (
        <BlockContent blocks={currentQuestion?.description} className={`${styles['sanity-block']}`} />
      )}
      <form className="flex flex-1" onSubmit={(e) => e.preventDefault()}>
        {renderActivityByQuestionType()}
      </form>
    </>
  )
}

export default QuizQuestionsWrapper
